import { type FilterOption } from "~/shared/section";
import { parseDateRangeValue } from "~/shared/ui/data-table/DataTableFilter";

/**
 * Канон слоя фильтров секций: перевод между URL-фильтрами (массивы значений) и черновиком
 * панели (одно значение на ключ), опции создателя и границы диапазона дат.
 *
 * До этого каждый слайс держал свои копии — вплоть до дословно одинаковых `asArray` в трёх
 * файлах. Копии расходились молча: правка формата даты в одном разделе не доезжала до
 * соседнего, а тесты одного слайса «покрывали» чужую копию только по имени.
 */

/** Одно значение на ключ: панель фильтров однозначная, URL — массивы (задел под мультивыбор). */
export type FilterDraft = Record<string, unknown>;

function asArray(value: unknown): readonly string[] {
    return typeof value === "string" && value ? [value] : [];
}

/** URL-фильтры (массивы) → черновик панели (первое значение каждого ключа). */
export function singleValueDraft<TFilters extends Record<string, readonly string[]>>(
    filters: TFilters,
): FilterDraft {
    return Object.fromEntries(Object.entries(filters).map(([key, values]) => [key, values[0]]));
}

/**
 * Черновик → URL-фильтры одной пачкой.
 *
 * Ключи перечисляются явно (обычно `Object.keys(DEFAULT_*_FILTERS)`): запись обязана
 * содержать ВСЕ ключи слайса, включая опустевшие, иначе снятый фильтр остался бы в URL.
 */
export function draftToFilterArrays<TFilters extends Record<string, readonly string[]>>(
    draft: FilterDraft,
    keys: readonly (keyof TFilters & string)[],
): Partial<Record<keyof TFilters & string, readonly string[]>> {
    return Object.fromEntries(keys.map((key) => [key, asArray(draft[key])])) as Partial<
        Record<keyof TFilters & string, readonly string[]>
    >;
}

export interface CreatorOptionSource {
    userId: string;
    name?: string | null;
    email?: string | null;
}

/** Участники пространства → опции фильтра «создатель», с откатом имя → почта → id. */
export function mapCreatorOptions(members: readonly CreatorOptionSource[]): FilterOption[] {
    return members.map((m) => ({
        value: m.userId,
        label: m.name?.trim() ? m.name : m.email?.trim() ? m.email : m.userId,
    }));
}

/** Сохранённое значение daterange-фильтра → включающие ISO-границы для API. */
export function dateRangeToBounds(value: string | undefined): { from?: string; to?: string } {
    const { from, to } = parseDateRangeValue(value);
    return {
        from: from ? `${from}T00:00:00` : undefined,
        to: to ? `${to}T23:59:59` : undefined,
    };
}

export { type FilterOption };
